import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';

// Local backend startup can outlive the frontend reconnect window after a server restart.
// Keep retries bounded, but long enough to bridge a cold boot for idempotent reads.
const RETRY_DELAYS_MS = [500, 1000, 2000, 3000, 5000, 8000, 10000];
const RETRYABLE_HTTP_STATUSES = new Set([502, 503, 504]);

const isFetchArgs = (args: string | FetchArgs): args is FetchArgs => typeof args !== 'string';

const requestMethod = (args: string | FetchArgs): string => {
  if (!isFetchArgs(args) || !args.method) {
    return 'GET';
  }
  return args.method.toUpperCase();
};

const isSafeToRetry = (args: string | FetchArgs): boolean => {
  const method = requestMethod(args);
  return method === 'GET' || method === 'HEAD';
};

const isAbortError = (error: FetchBaseQueryError): boolean =>
  error.status === 'FETCH_ERROR' &&
  typeof error.error === 'string' &&
  error.error.toLowerCase().includes('abort');

const isRetryableHttpStatus = (status: number): boolean => RETRYABLE_HTTP_STATUSES.has(status);

const isRetryableProxyParsingError = (error: FetchBaseQueryError): boolean => {
  if (error.status !== 'PARSING_ERROR') {
    return false;
  }

  const originalStatus =
    'originalStatus' in error && typeof error.originalStatus === 'number'
      ? error.originalStatus
      : null;
  return originalStatus !== null && isRetryableHttpStatus(originalStatus);
};

const isRetryableHttpError = (error: FetchBaseQueryError): boolean =>
  typeof error.status === 'number' && isRetryableHttpStatus(error.status);

export const shouldRetryTransientQueryError = (
  args: string | FetchArgs,
  error: FetchBaseQueryError,
  signalAborted = false
): boolean => {
  if (signalAborted || isAbortError(error)) {
    return false;
  }
  if (!isSafeToRetry(args)) {
    return false;
  }
  return (
    error.status === 'FETCH_ERROR' ||
    error.status === 'TIMEOUT_ERROR' ||
    isRetryableHttpError(error) ||
    isRetryableProxyParsingError(error)
  );
};

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

export const withTransientQueryRetry = <Result = unknown, DefinitionExtraOptions = {}, Meta = {}>(
  baseQuery: BaseQueryFn<
    string | FetchArgs,
    Result,
    FetchBaseQueryError,
    DefinitionExtraOptions,
    Meta
  >
): BaseQueryFn<string | FetchArgs, Result, FetchBaseQueryError, DefinitionExtraOptions, Meta> => {
  return async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: DefinitionExtraOptions
  ): Promise<QueryReturnValue<Result, FetchBaseQueryError, Meta>> => {
    let attempt = 0;

    for (;;) {
      const result = await baseQuery(args, api, extraOptions);
      if (!result.error) {
        return result;
      }
      if (
        attempt >= RETRY_DELAYS_MS.length ||
        !shouldRetryTransientQueryError(args, result.error, api.signal.aborted)
      ) {
        return result;
      }

      await sleep(RETRY_DELAYS_MS[attempt]);
      if (api.signal.aborted) {
        return result;
      }
      attempt += 1;
    }
  };
};
